import type { AutocompleteField as TAutocompleteField } from "src/components/Form/Form.types.ts";
import React, { useState } from "react";
import { useDebouncedValue } from "@exonn/hooks";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Flex } from "src/components/Flex.tsx";
import { Autocomplete } from "src/components/Autocomplete/Autocomplete.tsx";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { useSelect } from "src/system/useSelect.tsx";
import { ResourceListModal } from "src/components/ResourceListModal/ResourceListModal.tsx";
import { getSelectValue } from "src/system/getSelectValue.ts";

export const AutocompleteField = ({
  field,
  name,
}: {
  field: TAutocompleteField;
  name: string;
}) => {
  const form = useFormContext();
  const search = useWatch({
    name: field.searchFieldName,
  });

  const setSearch = (search: string) => {
    form.setValue(field.searchFieldName, search, { shouldDirty: true });
  };

  const { startSearchWith = 1 } = field;
  const [modalOpen, setModalOpen] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [debounced] = useDebouncedValue(search, 300);

  const {
    isFetching,
    isLoading: isDataLoading,
    isError,
    options,
    getResourceByValue,
  } = useSelect({
    resource: field.resource,
    onQueryFn: () => {
      setIsLoading(false);
    },
    disablePagination: true,
    labelProp: field.labelProp,
    valueProp: field.valueProp,
    include: field.include,

    filter: {
      items: [
        {
          field: field.filterField ?? "title",
          operator: field.filterOperator ?? "partial",
          value: debounced,
        },
      ],
    },
    queryOptions: {
      enabled: search?.length >= startSearchWith,
      staleTime: 0,
      gcTime: 0,
    },
  });

  const onSearchValueChangeHandle = (s: string) => {
    setSearch(s);
    setIsAutocompleteOpen(true);
    setIsLoading(true);
  };

  const onSearchHandleFromDataGrid = (search: string) => {
    setSearch(search);
  };

  return (
    <>
      <Controller
        key={name}
        name={name}
        render={({ field: { onChange, value }, formState: { errors } }) => {
          return (
            <Flex gap={10} alignItems={"flex-end"}>
              <Autocomplete
                startSearchWith={field.startSearchWith}
                isOpen={isAutocompleteOpen}
                setIsOpen={setIsAutocompleteOpen}
                style={{
                  flexGrow: 1,
                }}
                value={value}
                searchValue={search}
                isLoading={isLoading || isFetching || isDataLoading}
                isError={isError}
                onValueChange={(value) => {
                  onChange(value);
                  field.onValueChange?.({
                    value,
                    item: getResourceByValue(value),
                    form,
                  });
                }}
                onSearchValueChange={(s) => onSearchValueChangeHandle(s)}
                placeholder={field.placeholder}
                options={options}
                label={field.label}
                error={errors?.root?.[name]?.message}
              />
              <ButtonV2
                style={{
                  width: "172px",
                }}
                size={"sm"}
                startAdornment={<i className={"icon-15-search"} />}
                onClick={() => setModalOpen(true)}
              >
                Advanced search
              </ButtonV2>
            </Flex>
          );
        }}
      />
      <ResourceListModal
        resource={field.resource}
        isModalOpen={modalOpen}
        onModalOpen={setModalOpen}
        onRowClick={(params) => {
          form.setValue(
            name,
            getSelectValue({
              item: params.row,
              valueProp: field.valueProp,
            }),
          );
          field?.advancedSearch?.onRowClick?.({
            form,
            params,
          });
          const searchProp =
            field?.advancedSearch?.labelProp ?? field.filterField;

          onSearchHandleFromDataGrid(params.row[searchProp]);
        }}
      />
    </>
  );
};
